import { snakeCaseObject } from '@edx/frontend-platform';

import { validatePasswordField } from '../../data/utils/passwordValidator';
import messages from '../messages';
import validateEmail from '../RegistrationFields/EmailField/validator';
import validateFullName from '../RegistrationFields/NameField/validator';

/**
 * It validates all fields except email and password.
 * @param payload
 * @param errors
 * @param configurableFormFields
 * @param fieldDescriptions
 * @param formatMessage
 * @returns {{fieldErrors, isValid: boolean}}
 */
export const validateRegistrationForm  = (payload, errors, formatMessage) => {
  const fieldErrors = { ...errors };
  let isValid = true;
  let emailSuggestion = { suggestion: '', type: '' };

  // basic name fields
   if ('full_name' in payload){
    
    fieldErrors.full_name = validateFullName(payload.full_name, formatMessage)
    if (fieldErrors.full_name) { isValid = false; }
  }

  // Validate email
  if ('email' in payload) {
    const {
      fieldError, confirmEmailError, suggestion,
    } = validateEmail(payload.email, '', formatMessage);
    if (fieldError) {
      fieldErrors.email = fieldError;
      isValid = false;
    }
    if (confirmEmailError) {
      fieldErrors.confirm_email = confirmEmailError;
      isValid = false;
    }
    emailSuggestion = suggestion;
  }

  // Validate password
  // If the payload contains 'social_auth_provider', we skip password validation
  // because social authentication does not require the user to insert a password.  
  if (!('social_auth_provider' in payload)) {
    ['password', 'confirm_password'].forEach(field => {
      if (field in payload) {
        fieldErrors[field] = validatePasswordField(payload[field], formatMessage);
        if (fieldErrors[field]) isValid = false;
      }
    });
  }


  // terms of service
  if (!payload.terms_of_service) {
    fieldErrors.terms_of_service = formatMessage(messages['unchecked.terms.of.service.error']);
    isValid = false;
  } else {
    fieldErrors.terms_of_service = '';
  }

  return { isValid, fieldErrors, emailSuggestion };
};

/**
 * It prepares a payload for registration data that can be passed to registration API endpoint.
 * @param initPayload
 * @param showMarketingEmailOptInCheckbox
 * @param totalRegistrationTime
 * @param queryParams
 * @param configurableFormFields
 * @returns {*}
 */
export const prepareRegistrationPayload = (
  initPayload,
  showMarketingEmailOptInCheckbox,
  totalRegistrationTime,
  queryParams,
  configurableFormFields,
) => {
  let payload = { ...initPayload };

  if (configurableFormFields) {
    Object.keys(configurableFormFields).forEach((fieldName) => {
      if (fieldName === 'country') {
        payload[fieldName] = configurableFormFields[fieldName].countryCode;
      } else {
        payload[fieldName] = configurableFormFields[fieldName];
      }
    });
  }

  // Don't send the marketing email opt-in value if the flag is turned off
  if (!showMarketingEmailOptInCheckbox) {
    delete payload.marketingEmailsOptIn;
  }

  payload = snakeCaseObject(payload);
  payload.totalRegistrationTime = totalRegistrationTime;

  // add query params to the payload
  payload = { ...payload, ...queryParams };
  return payload;
};

/**
 * A helper for backend validations selector. It processes the api output and generates a
 * key value dict for field errors.
 * @param registrationError
 * @param validations
 * @returns {{username: string}|{name: string}|*|{}|null}
 */
export const getBackendValidations = (registrationError, validations) => {
  if (validations) {
    const fieldErrors = {};
    Object.keys(validations.validationDecisions).forEach(field => {
      const fieldError = validations.validationDecisions[field];    
      if (typeof fieldError === 'object' && fieldError !== null) {
        fieldErrors[field] = fieldError.errorMessage;
        if (fieldError.errorCode) {
          fieldErrors[`${field}_errorCode`] = fieldError.errorCode;
        }
      } else {
        fieldErrors[field] = fieldError;
      }
    });
    return fieldErrors;
  }

  if (Object.keys(registrationError).length > 0) {
    const fields = Object.keys(registrationError).filter(
      (fieldName) => !(fieldName in ['errorCode', 'usernameSuggestions']),
    );

    const validationDecisions = {};
    fields.forEach(field => {
      validationDecisions[field] = registrationError[field][0].userMessage || '';
    });
    return validationDecisions;
  }

  return null;
};

export const frameSafeRedirect = (url) => {
  if (window.self !== window.top) {
    window.top.location.href = url;
  } else {
    window.location.href = url;
  }
};

export const sanitizeToEnglishKey = (str) =>
  str
    .toLowerCase()
    .replace(/[^a-z\s-]/g, '')
    .replace(/\s+/g, '_'); 


export const getProviderObjLabel = (
  providerObj,
  messages,
  formatMessage,
  sanitize = sanitizeToEnglishKey
) => {
  const keySuffix = sanitize(providerObj.name || '');
  const providerKey = `provider.${keySuffix}`;
  const descriptor =
    messages[providerKey] ??
    messages['provider.generic'] ?? { id: 'provider.generic', defaultMessage: '{providerName}' };

  if (!messages[providerKey]) {
    console.warn(`CampusIL Dev-team: No translation found for provider: "${providerObj.name}". Please add key "${providerKey}" to messages.js.`);
  }

  return formatMessage(descriptor, { providerName: providerObj.name });
};
